// new component which just lists the patients:

import { useEffect, useState } from "react";
import { PatientsContext } from "../../stores/Patients/patients.provider";
import useStore from "../../helpers/useStore";
import { 
  Button, 
  Link, 
  List, 
  ListItem, 
  ListItemText, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  CircularProgress
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PatientCommands } from "../../stores/Patients/patient.commands";
import { Archive, DeleteForever } from "@mui/icons-material";
import { formatDateDisplay } from "../../helpers/dateFormat";

export const PatientList = () => {
  const PatientStore = useStore(PatientsContext);

  const empty: any[] = [];
  const [patients, setPatients] = useState(empty);
  const [loading, setLoading] = useState(true);
  const { openPatients:selectedPatients } = PatientStore;
  let [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { navigateToPatient } = PatientCommands(PatientStore);

  // Zustände für die Dialoge
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<any>(null);
  const [confirmationText, setConfirmationText] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Patienten-ID-Array aus den ausgewählten Patienten
  const selectedIds = selectedPatients.map((p:any) => p.id);

  useEffect(() => {
    const fetchPatients = async () => {
      setLoading(true);
      const { data } = await PatientStore.apiClient.getStats();
      const patients = data as unknown as any[];
      setPatients(patients);
      setLoading(false);
    };

    fetchPatients();
  }, []);

  // Archivieren-Dialog öffnen
  const openArchiveDialog = (patient: any, event: React.MouseEvent) => {
    event.stopPropagation(); // Verhindert das Öffnen der Patientendetails
    setSelectedPatient(patient);
    setArchiveDialogOpen(true);
  };

  // Löschen-Dialog öffnen
  const openDeleteDialog = (patient: any, event: React.MouseEvent) => {
    event.stopPropagation(); // Verhindert das Öffnen der Patientendetails
    setSelectedPatient(patient);
    setDeleteDialogOpen(true);
  };

  // Patient archivieren
  const archivePatient = async () => {
    if (!selectedPatient) return;
    
    setActionLoading(true);
    try {
      await PatientStore.archivePatient(selectedPatient.patient_id);
      // Liste aktualisieren
      const updatedPatients = patients.filter(p => p.patient_id !== selectedPatient.patient_id);
      setPatients(updatedPatients);
      setArchiveDialogOpen(false);
    } catch (error) {
      console.error("Fehler beim Archivieren des Patienten:", error);
    } finally {
      setActionLoading(false);
    }
  };

  // Patient löschen
  const deletePatient = async () => {
    if (!selectedPatient || confirmationText !== 'Löschen') return;
    
    setActionLoading(true);
    try {
      await PatientStore.permanentlyDeletePatient(selectedPatient.patient_id, confirmationText);
      // Liste aktualisieren
      const updatedPatients = patients.filter(p => p.patient_id !== selectedPatient.patient_id);
      setPatients(updatedPatients);
      setDeleteDialogOpen(false);
    } catch (error: any) {
      console.error("Fehler beim Löschen des Patienten:", error);
      // Zeige detailliertere Fehlermeldung an
      const errorMessage = error?.response?.data?.message || "Ein unbekannter Fehler ist aufgetreten";
      alert(`Fehler beim Löschen des Patienten: ${errorMessage}`);
    } finally {
      setActionLoading(false);
      setConfirmationText('');
    }
  };

  // a table of patients
  return (
    <div>
      <CopaymentChecker />
      {loading ? (
        <p>Loading...</p>
      ) : (
        <TableContainer>
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell>Id</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Geburtsdatum</TableCell>
                <TableCell>Rezepte</TableCell>
                <TableCell>Geplante Therapien</TableCell>
                <TableCell>Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {patients.map((row) => (
                <TableRow
                  key={row.name}
                  sx={{cursor:"pointer", hover: "#000", background: selectedIds.includes(row.patient_id) ? "yellow" : "white" }}
                  onClick={() => {
                    // Mark selection in store + navigate using projection helper
                    navigateToPatient(navigate, row.patient_id, null,'prescriptions');
                  }}
                >
                  <TableCell component="th" scope="row">
                    {row.patient_id}
                  </TableCell>
                  <TableCell>
                    {row.patient_lastName}, {row.patient_firstName}
                  </TableCell>
                  <TableCell>{row.patient_dob ? formatDateDisplay(row.patient_dob) : ""}</TableCell>
                  <TableCell>{row.rxcount}</TableCell>
                  <TableCell>{row.treatmentcount}</TableCell>
                  <TableCell>
                    <Tooltip title="Patient archivieren">
                      <IconButton 
                        size="small" 
                        color="primary"
                        onClick={(e) => openArchiveDialog(row, e)}
                        sx={{ mr: 1 }}
                      >
                        <Archive fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Patient endgültig löschen">
                      <IconButton 
                        size="small" 
                        color="error"
                        onClick={(e) => openDeleteDialog(row, e)}
                      >
                        <DeleteForever fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Archivieren Dialog */}
      <Dialog
        open={archiveDialogOpen}
        onClose={() => setArchiveDialogOpen(false)}
      >
        <DialogTitle sx={{ bgcolor: '#fff3e0', color: '#e65100' }}>
          Patient archivieren
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mt: 2 }}>
            Möchtest du den Patienten <strong>{selectedPatient?.patient_lastName}, {selectedPatient?.patient_firstName}</strong> wirklich archivieren?
            <br /><br />
            Der Patient wird aus der Hauptliste entfernt und ins Archiv verschoben. Archivierte Patienten können jederzeit aus dem Archiv wiederhergestellt werden.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => setArchiveDialogOpen(false)} 
            variant="outlined"
          >
            Abbrechen
          </Button>
          <Button 
            onClick={archivePatient} 
            color="primary" 
            variant="contained"
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={18} /> : <Archive />}
          >
            {actionLoading ? 'Wird archiviert...' : 'Archivieren'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Löschen Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle sx={{ bgcolor: '#ffebee', color: '#c62828' }}>
          Patient endgültig löschen
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mt: 2 }}>
            <strong>WARNUNG: Diese Aktion kann nicht rückgängig gemacht werden!</strong>
            <br /><br />
            Möchten Sie den Patienten <strong>{selectedPatient?.patient_lastName}, {selectedPatient?.patient_firstName}</strong> unwiderruflich löschen?
            <br /><br />
            Hinweis: Wenn der Patient noch mit Terminen oder Rezepten verknüpft ist, kann die Löschung nicht durchgeführt werden.
            <br /><br />
            Um fortzufahren, geben Sie bitte genau "Löschen" in das Feld unten ein:
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label='Zur Bestätigung "Löschen" eingeben'
            fullWidth
            value={confirmationText}
            onChange={(e) => setConfirmationText(e.target.value)}
            error={confirmationText !== '' && confirmationText !== 'Löschen'}
            helperText={confirmationText !== '' && confirmationText !== 'Löschen' ? 'Bitte genau "Löschen" eingeben' : ''}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => {
              setDeleteDialogOpen(false);
              setConfirmationText('');
            }}
            variant="outlined"
          >
            Abbrechen
          </Button>
          <Button 
            onClick={deletePatient} 
            color="error" 
            variant="contained"
            disabled={confirmationText !== 'Löschen' || actionLoading}
            startIcon={actionLoading ? <CircularProgress size={18} /> : <DeleteForever />}
          >
            {actionLoading ? 'Wird gelöscht...' : 'Endgültig löschen'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};


interface DataItem {
  rxId: string,
  rxType: string,
  rxDate: string,
  patientId: string,
  rxNumber: string,
  dueAmount: string,
}

const CopaymentChecker: React.FC = () => {
  const PatientStore = useStore(PatientsContext);

  const [data, setData] = useState<DataItem[]>([]);
  const [loading, setLoading] = useState(false);

  // Fetch function to load data from the API
  const loadData = async (): Promise<void> => {
    setLoading(true);
    try {
      const {data} =  await PatientStore.apiClient.getRxStats();; // Replace with your endpoint
      const items = data as unknown as any[]
      setData(items);
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Button variant="contained" onClick={loadData} disabled={loading}>
        {loading ? 'Loading...' : 'Unbezahlte Rezepte anzeigen'}
      </Button>
      <List>
        {data.map(item => (
          <ListItem key={item.rxId}>
            <ListItemText
              primary={
                <>
                  <Link
                    href={`/patient?pId=${item.patientId}&bill`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {item.rxNumber}
                  </Link>{' '}
                  ({item.rxType})
                </>
              }
              secondary={`ID: ${item.rxId} | Date: ${item.rxDate} | Patient ID: ${item.patientId} | Due Amount: ${item.dueAmount}`}
            />
          </ListItem>
        ))}
      </List>
    </div>
  );
};